'use client'

import { useCallback, useEffect, useState } from 'react'
import { CheckCircle2, AlertCircle, AlertTriangle, X } from 'lucide-react'

type ToastKind = 'success' | 'error' | 'warning'
type Toast = { id: number; kind: ToastKind; message: string; description?: string }
const eventName = 'chatsyncs:partner-toast'
let sequence = 0
function announce(kind: ToastKind, message: string, description?: string) {
  if (typeof window === 'undefined' || !message.trim()) return
  window.dispatchEvent(new CustomEvent<Toast>(eventName, { detail: { id: ++sequence, kind, message, description } }))
}
export const partnerToast = {
  success: (message: string, description?: string) => announce('success', message, description),
  error: (message: string, description?: string) => announce('error', message, description),
  warning: (message: string, description?: string) => announce('warning', message, description),
}

function ToastCard({ toast, dismiss }: { toast: Toast; dismiss: (id: number) => void }) {
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  // Failures remain visible until dismissed. Success pauses while being read.
  useEffect(() => {
    if (toast.kind !== 'success' || hovered || focused) return
    const timer = window.setTimeout(() => dismiss(toast.id), 6000)
    return () => window.clearTimeout(timer)
  }, [toast.id, toast.kind, hovered, focused, dismiss])
  const Icon = toast.kind === 'success' ? CheckCircle2 : toast.kind === 'error' ? AlertCircle : AlertTriangle
  const iconColor = toast.kind === 'success' ? 'text-success' : toast.kind === 'error' ? 'text-chip-red-ink' : 'text-chip-amber-ink'
  return <div role={toast.kind === 'error' ? 'alert' : 'status'} aria-atomic="true"
    onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
    onFocusCapture={() => setFocused(true)} onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false) }}
    className="pointer-events-auto flex items-start gap-2.5 rounded-lg bg-admin-canvas p-3 text-ink shadow-admin-float animate-admin-toast-in">
    <Icon className={`mt-0.5 size-4 shrink-0 ${iconColor}`} aria-hidden="true"/>
    <div className="min-w-0 flex-1"><p className="break-words text-admin-sm font-medium text-ink">{toast.message}</p>{toast.description && <p className="mt-0.5 break-words text-admin-sm text-ink-muted">{toast.description}</p>}</div>
    <button type="button" aria-label="Dismiss notification" onClick={() => dismiss(toast.id)} className="-m-0.5 shrink-0 rounded p-0.5 text-ink-faint hover:bg-admin-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"><X className="size-3.5" aria-hidden="true"/></button>
  </div>
}
export default function PartnerToasts() {
  const [toasts, setToasts] = useState<Toast[]>([])
  const dismiss = useCallback((id: number) => setToasts(current => current.filter(toast => toast.id !== id)), [])
  useEffect(() => {
    const receive = (event: Event) => {
      const toast = (event as CustomEvent<Toast>).detail
      setToasts(current => [...current.filter(item => item.kind !== toast.kind || item.message !== toast.message), toast].slice(-3))
    }
    window.addEventListener(eventName, receive)
    return () => window.removeEventListener(eventName, receive)
  }, [])
  return <aside aria-label="Action notifications" className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex w-full flex-col gap-2 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:left-auto sm:max-w-sm">{toasts.map(toast => <ToastCard key={toast.id} toast={toast} dismiss={dismiss}/>)}</aside>
}
